import { Inject, Injectable, NgZone, OnDestroy, Optional } from '@angular/core';
import { LostReasonsServiceProxy } from '@shared/service-proxies/service-proxies';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';

import { LEAD_PIPELINE_PUSHES, LeadPipelinePushes } from '../lead-lifecycle-client.service';
import { LostReasonCatalog, catalogFromSettings, legacyLostReasonCatalog, lostReasonsEnabled } from './lost-reason-catalog';

/**
 * The tenant's lost reasons for every "Mark lost" picker in this tab (story rule 17; LEAD_LOST_REASONS_PLAN.md §4): ONE list, read
 * from `LostReasons/GetLostReasonSettings` - the reasons in settings order, the require switch and the Lost stages' defaults.
 *
 * - Feature off: MD's six fixed reasons, never a request (`legacyLostReasonCatalog`).
 * - Feature on: the six until the first read answers (a pick made before it is sent as MD's code, which the server maps to the same
 *   reason), then the tenant's list. It reloads on every `lostReasons` push from another tab or user (rule 23) and when this tab's
 *   own Settings change it (`refresh`), so an open picker changes at once.
 *
 * A failed read keeps the last list (pickers never go empty). Root-provided: the list outlives every panel.
 */
@Injectable({ providedIn: 'root' })
export class LostReasonCatalogService implements OnDestroy {
    private readonly state = new BehaviorSubject<LostReasonCatalog>(legacyLostReasonCatalog());
    private readonly reload$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    /** The current list; emits at once and on every change. */
    readonly catalog$: Observable<LostReasonCatalog> = this.state.asObservable();

    constructor(
        private readonly lostReasonsService: LostReasonsServiceProxy,
        private readonly zone: NgZone,
        @Optional() @Inject(LEAD_PIPELINE_PUSHES) pushes?: LeadPipelinePushes | null
    ) {
        if (!lostReasonsEnabled()) {
            return;
        }
        this.reload$
            .pipe(
                switchMap(() => this.load()),
                takeUntil(this.destroy$)
            )
            .subscribe((catalog) => {
                if (catalog) {
                    this.state.next(catalog);
                }
            });
        if (pushes && pushes.lostReasons$) {
            // The push fires outside Angular's zone (UserSignalrService): re-enter it, or the reload and the open pickers' re-render
            // wait for the next unrelated change detection (seen on test 2026-10-23: another tab's picker kept the old name).
            pushes.lostReasons$.pipe(takeUntil(this.destroy$)).subscribe(() => this.zone.run(() => this.refresh()));
        }
        this.refresh();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** The list as it is now (a picker opening reads this; it subscribes for later changes). */
    current(): LostReasonCatalog {
        return this.state.value;
    }

    /** Re-reads the tenant's list (Settings saved in this tab, or a push). A no-op while the feature is off. */
    refresh(): void {
        if (lostReasonsEnabled()) {
            this.reload$.next();
        }
    }

    private load(): Observable<LostReasonCatalog | null> {
        return this.lostReasonsService.getLostReasonSettings().pipe(
            map((result) => (result && !result.isError && result.responseData ? catalogFromSettings(result.responseData) : null)),
            catchError(() => of(null))
        );
    }
}
